"use client";

import React from "react";
import { ArrowRight, Building2, CircleCheck as CheckCircle2, Droplets, Flame, Hammer, HardHat, PhoneCall, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface RelatedServiceItem {
  title: string;
  description: string;
  to: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  badge: string;
  iconName: "droplets" | "flame" | "building";
}
const relatedServices: RelatedServiceItem[] = [{
  title: "Water Damage & Flood Restoration",
  description: "Rapid water extraction, structural desiccant drying, and real-time infrared moisture mapping across flooded spaces.",
  to: "/WaterDamageFloodRestorationPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/water_damage_mitigation_crew.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/water_damage_mitigation_crew.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/water_damage_mitigation_crew.png-wvc-sizes",
  badge: "24/7 Rapid Containment",
  iconName: "droplets"
}, {
  title: "Fire, Smoke & Odor Restoration",
  description: "Controlled soot removal, thermal fogging deodorization, and structural cleaning before wall framing commences.",
  to: "/FireSmokeOdorRestorationPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/fire_smoke_restoration_cleaning.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/fire_smoke_restoration_cleaning.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/fire_smoke_restoration_cleaning.png-wvc-sizes",
  badge: "Structural Deodorization",
  iconName: "flame"
}, {
  title: "Commercial & HOA Property Restoration",
  description: "Scalable multi-family and commercial disaster response designed to maintain tenant occupancy and insurance compliance.",
  to: "/CommercialHOAPropertyRestorationPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_hoa_property_restoration.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_hoa_property_restoration.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_hoa_property_restoration.png-wvc-sizes",
  badge: "Multi-Unit Response",
  iconName: "building"
}];
const highlights = ["Zero gap between emergency dry-out and structural framing", "Single unified insurance estimate and adjuster coordination", "Direct local carpenters, drywallers, and project leads", "All regional permits, architectural plans, and code sign-offs managed"];
export default function WhyChoose() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="why-choose-triserve-related-services" data-nav="dark" className="dark bg-background text-foreground py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Main Section Banner Container */}
        <div className="rounded-2xl border border-secondary/50 bg-secondary text-secondary-foreground p-6 sm:p-10 lg:p-14 shadow-xl">
          {/* Top Technical Spec Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-secondary-foreground/15 pb-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-secondary-foreground/20 bg-secondary-foreground/10 px-3 py-1 text-xs font-semibold tracking-wider uppercase text-secondary-foreground">
              <ShieldCheck className="size-3.5 text-accent" />
              Single-Source Contractor Guarantee
            </div>
            <div className="flex items-center gap-2 font-mono text-xs text-secondary-foreground/80">
              <span className="inline-block size-2 rounded-full bg-accent animate-pulse" />
              Direct Deployment: MD, DC &amp; Northern VA
            </div>
          </div>

          {/* Primary Split Narrative */}
          <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
            {/* Left Narrative Column */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUpVariant} className="lg:col-span-7 space-y-6">
              <div className="space-y-3">
                <span className="font-mono text-xs uppercase tracking-widest text-accent font-semibold">
                  Eliminating Post-Disaster Delays
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight text-secondary-foreground">
                  One team from initial tear-out to final trim.
                </h2>
                <p className="text-base sm:text-lg text-secondary-foreground/90 leading-relaxed font-sans">
                  You never have to find, vet, or schedule secondary remodelers
                  once mitigation finishes.
                </p>
              </div>

              <p className="text-sm sm:text-base text-secondary-foreground/80 leading-relaxed">
                Disaster recovery typically falls apart when water and fire
                mitigation ends, leaving property owners scrambling for drywall
                contractors, framers, and finish trades. TriServe eliminates
                that break. Because our rebuild teams assess timber, MEP paths,
                and architectural constraints during demolition, your rebuild
                starts the day drying clearance is signed.
              </p>

              {/* Core Check Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {highlights.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-secondary-foreground/90">
                    <CheckCircle2 className="size-4 shrink-0 text-accent mt-0.5" />
                    <span>{item}</span>
                  </div>)}
              </div>

              {/* Action Triggers */}
              <div className="flex flex-wrap items-center gap-4 pt-4">
                <Button asChild className="bg-accent text-accent-foreground font-bold hover:bg-accent/90 shadow-md h-11 px-6 rounded-md">
                  <Link to="/ContactRequestServicePage">
                    Schedule Structural Assessment
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" className="border-secondary-foreground/30 bg-transparent text-secondary-foreground hover:bg-secondary-foreground/10 h-11 px-5 rounded-md">
                  <a href="tel:855-435-6631">
                    <PhoneCall className="size-4 text-accent" />
                    Direct Dispatch: 855-435-6631
                  </a>
                </Button>
              </div>
            </motion.div>

            {/* Right Photo Column */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUpVariant} className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-xl border border-secondary-foreground/20 bg-secondary/80 shadow-2xl">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/framing_crew_installation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/framing_crew_installation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/framing_crew_installation.png-wvc-sizes" alt="TriServe active framing crew installing structural timber studs on site" loading="lazy" decoding="async" className="h-72 sm:h-96 w-full object-cover" />

                {/* Technical Framing Badge Overlay */}
                <div className="absolute bottom-3 left-3 right-3 rounded-lg border border-secondary-foreground/20 bg-background/90 p-3 backdrop-blur-xs text-foreground shadow-md">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="rounded-md bg-primary/15 p-1.5 text-primary">
                        <Hammer className="size-4" />
                      </div>
                      <div>
                        <p className="text-xs font-bold leading-tight font-default">
                          Active Structural Framing
                        </p>
                        <p className="text-[11px] text-muted-foreground font-mono">
                          Inspected studs, trusses &amp; headers
                        </p>
                      </div>
                    </div>
                    <span className="rounded-sm bg-accent/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-foreground">
                      Certified
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Related Services Sub-Header */}
          <div className="mt-14 pt-8 border-t border-secondary-foreground/15">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
              <div>
                <span className="font-mono text-xs uppercase tracking-widest text-accent font-semibold">
                  Integrated Damage Recovery
                </span>
                <h3 className="text-xl sm:text-2xl font-bold font-default text-secondary-foreground mt-1">
                  Connected restoration services we coordinate
                </h3>
              </div>
              <p className="text-xs sm:text-sm text-secondary-foreground/75 max-w-md">
                Rebuilding begins with precise containment. Our local crews
                cross-operate across all initial mitigation stages.
              </p>
            </div>

            {/* Service Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedServices.map((service, i) => {
              const IconComponent = service.iconName === "droplets" ? Droplets : service.iconName === "flame" ? Flame : Building2;
              return <div key={service.to} data-index={i}>
                    <Card className="group h-full flex flex-col justify-between overflow-hidden border border-secondary-foreground/20 bg-card text-card-foreground shadow-md transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-xl rounded-xl">
                      <div>
                        {/* Image Header with Spec Pill */}
                        <div className="relative h-44 w-full overflow-hidden bg-muted">
                          <img src={service.imageSrc} data-wvc-srcset={service.imageSrcset} data-wvc-sizes={service.imageSizes} alt={service.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-md border border-border/80 bg-background/90 px-2.5 py-1 text-[11px] font-medium text-foreground backdrop-blur-xs">
                            <IconComponent className="size-3 text-primary" />
                            {service.badge}
                          </div>
                        </div>

                        <CardContent className="p-5">
                          <h4 className="text-base sm:text-lg font-bold font-default text-card-foreground group-hover:text-primary transition-colors">
                            {service.title}
                          </h4>
                          <p className="mt-2 text-xs sm:text-sm text-muted-foreground leading-relaxed line-clamp-3">
                            {service.description}
                          </p>
                        </CardContent>
                      </div>

                      <div className="px-5 pb-5 pt-0">
                        <Button asChild variant="ghost" className="w-full justify-between px-3 py-2 text-xs font-semibold text-primary hover:bg-primary/10 hover:text-primary rounded-md">
                          <Link to={service.to}>
                            Explore Service Details
                            <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                          </Link>
                        </Button>
                      </div>
                    </Card>
                  </div>;
            })}
            </div>
          </div>

          {/* Bottom Technical Spec Bar */}
          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-secondary-foreground/15 pt-5 text-xs font-mono text-secondary-foreground/75">
            <div className="flex items-center gap-2">
              <HardHat className="size-4 text-accent" />
              <span>Full Mid-Atlantic Class A General Contracting Licenses</span>
            </div>
            <div className="flex items-center gap-3">
              <span>Direct Estimating Software: Xactimate®</span>
              <span className="hidden sm:inline text-secondary-foreground/40">•</span>
              <span className="hidden sm:inline">24/7 Field Supervisors</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}